import { useMemo, useRef, useState } from "react";
import { generatePath, useOutletContext, useParams } from "react-router";
import { styled } from "@pigment-css/react";
import { QuickFilterIcon, SearchIcon } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { OrderInvoiceStatus } from "@acto/gql/graphql";
import { AnalyticsEvents, kpiToMetric, useTrack } from "@acto/taskpane/core/analytics";
import { toSchemaKpi, toTimeframe } from "@acto/taskpane/core/chat-envelope";
import { Kpi } from "@acto/taskpane/core/domain";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { useDebouncedSearchTerm } from "@acto/taskpane/core/utils/hooks/useDebouncedSearchTerm";
import { OrderRows } from "@acto/taskpane/field-sales-2/components/patterns/OrderRows";
import { PageLayout } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import { SearchHeaderRow } from "@acto/taskpane/field-sales-2/components/patterns/Search";
import { FilterHeaderRow } from "./FilterHeaderRow";
import { ShellCloseButton } from "@acto/taskpane/field-sales-2/components/patterns/ShellTopBar/ShellTopBar";
import {
  RowListCard,
  RowListEmptyState,
  RowSkeletonList,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { FilterPopover } from "@acto/taskpane/field-sales-2/components/patterns/FilterPopover";
import { HeaderIconButton } from "@acto/taskpane/field-sales-2/components/primitives/HeaderIconButton";
import { KpiPairCard } from "@acto/taskpane/field-sales-2/components/primitives/KpiPairCard";
import { ListTitle } from "@acto/taskpane/field-sales-2/components/primitives/ProductTrendList";
import { SelectMenu } from "@acto/taskpane/field-sales-2/components/primitives/SelectMenu";
import { TitlePill } from "@acto/taskpane/field-sales-2/components/primitives/TitlePill";
import type { CustomerOutletContext } from "@acto/taskpane/field-sales-2/CustomerDetailsLayout";
import { useClientOrders } from "@acto/taskpane/field-sales-2/data-access/useClientOrders";
import { useCustomerOrdersTrend } from "@acto/taskpane/field-sales-2/data-access/useCustomerOrdersTrend";
import { useNavigateWithReturn } from "@acto/taskpane/field-sales-2/navigation";
import { ShellSlotContent } from "@acto/taskpane/field-sales-2/shell";
import { useScrolledPast } from "@acto/taskpane/field-sales-2/utils/hooks/useScrolledPast";
import { useContributeCustomerDetailScope } from "@acto/taskpane/field-sales-2/utils/useContributeCustomerDetailScope";
import { useInfiniteScroll } from "@acto/taskpane/field-sales-2/pages/useInfiniteScroll";
import { AnalysisDrillNav } from "./AnalysisDrillNav";
import { buildAnalysisFilters } from "./buildAnalysisFilters";
import { OrdersProductPath, useOrdersProductList } from "./OrdersProductPath";
import { useProductDrill } from "./ProductDrillContext";
import { ListHeaderAnchor, SHELL_CONTROL_ROW_BOTTOM_PX } from "./shellControlRow";
import { useDelayedViewTrack } from "./useDelayedViewTrack";

const ORDERS_LIST = 0;
const PRODUCT_GROUPS_LIST = 1;

const STATUS_FILTERS = [
  undefined,
  OrderInvoiceStatus.Invoiced,
  OrderInvoiceStatus.PartiallyInvoiced,
  OrderInvoiceStatus.Open,
] as const;

const Leading = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  minWidth: 0,
}));

const PillFade = styled("div")({
  minWidth: 0,
  opacity: 0,
  transform: "translateX(-8px)",
  transition: "opacity 0.2s ease, transform 0.2s ease",
  pointerEvents: "none",
  '&[data-visible="true"]': {
    opacity: 1,
    transform: "translateX(0)",
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
});

// No transform: a fixed-position descendant must still cover the viewport. Hidden, it takes no width.
const DockFade = styled("div")({
  opacity: 0,
  pointerEvents: "none",
  maxWidth: 0,
  overflow: "hidden",
  transition: "opacity 0.2s ease",
  '&[data-visible="true"]': {
    opacity: 1,
    pointerEvents: "auto",
    maxWidth: "none",
    overflow: "visible",
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
});

const TrailingActions = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
}));

const TitleIndent = styled("div")(({ theme }) => ({
  paddingLeft: theme.fieldSalesTwo.spacing[16],
  minWidth: 0,
}));

export function CustomerOrdersAnalysis() {
  const { id } = useParams<{ id: string }>();
  const t = useTranslations("fieldSalesTwo.analysis.orders");
  const tAnalysis = useTranslations("fieldSalesTwo.analysis");
  const tSearch = useTranslations("fieldSalesTwo.search");
  const push = useNavigateWithReturn();
  const track = useTrack();

  const {
    customer,
    isPending: isCustomerPending,
    dateRange,
    isCustomRange,
    selection,
    ordersList,
    onOrdersListChange,
  } = useOutletContext<CustomerOutletContext>();

  const analysisFragment = useMemo(
    () => ({ selected_kpi: toSchemaKpi(Kpi.ORDERS), ...toTimeframe(selection) }),
    [selection],
  );
  useContributeCustomerDetailScope("analysis", analysisFragment);

  const { trend, orderCount } = useCustomerOrdersTrend(id);
  const { stack, drillTo } = useProductDrill();
  const isDrilled = stack.length > 0;
  const currentLevel = stack.at(-1);
  const selectedProduct = currentLevel?.kind === "product" ? currentLevel : undefined;
  const showsProductGroups = ordersList === "productGroups" && !selectedProduct;
  const [statusFilterIndex, setStatusFilterIndex] = useState(0);
  const statusFilter = STATUS_FILTERS[statusFilterIndex];
  const {
    displayValue: searchValue,
    setDisplayValue: setSearchValue,
    debouncedValue: orderNumber,
    reset: resetSearch,
  } = useDebouncedSearchTerm();
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const [isProductSearchOpen, setIsProductSearchOpen] = useState(false);
  const [shownLevelId, setShownLevelId] = useState(currentLevel?.id);
  if (shownLevelId !== currentLevel?.id) {
    setShownLevelId(currentLevel?.id);
    setStatusFilterIndex(0);
    resetSearch();
    setIsSearchOpen(false);
    setIsProductSearchOpen(false);
  }
  const levelTrend = useCustomerOrdersTrend(id, currentLevel?.id);
  const productList = useOrdersProductList({
    clientId: id,
    parent: selectedProduct ? undefined : currentLevel,
    dateRange,
    enabled: showsProductGroups,
  });
  const productListHeaderRef = useRef<HTMLDivElement>(null);
  const productFilterRef = useRef<HTMLButtonElement>(null);
  const [isProductFilterOpen, setIsProductFilterOpen] = useState(false);
  const listHeaderRef = useRef<HTMLDivElement>(null);
  const orders = useClientOrders(
    showsProductGroups ? undefined : id,
    dateRange,
    statusFilter ? [statusFilter] : undefined,
    orderNumber.trim(),
    selectedProduct?.id,
  );
  const orderRows = orders.items.map((order) => ({
    ...order,
    statusLabel: order.invoiceStatus ? t(`invoiceStatus.${order.invoiceStatus}`) : undefined,
  }));
  const statusFilterLabel = t(`invoiceStatus.${statusFilter ?? "ALL"}`);
  const sentinelRef = useInfiniteScroll<HTMLLIElement>({
    hasMore: orders.hasMore,
    isLoadingMore: orders.isLoadingMore,
    loadMore: orders.loadMore,
    resetKey: orders.items.length,
  });

  const { observe: observeFigures, scrolledPast: isPastFigures } =
    useScrolledPast<HTMLDivElement>(SHELL_CONTROL_ROW_BOTTOM_PX);
  const { observe: observeListHeader, scrolledPast: isPastListHeader } =
    useScrolledPast<HTMLDivElement>(SHELL_CONTROL_ROW_BOTTOM_PX);

  useDelayedViewTrack(
    () => {
      if (!customer || !dateRange) return;
      track(AnalyticsEvents.CUSTOMER_ANALYSIS_VIEWED, {
        customerId: customer.id,
        metric: kpiToMetric(Kpi.ORDERS),
        filters: buildAnalysisFilters({ dateRange, isCustomRange, currentGroup: undefined, trends: undefined }),
      });
    },
    `${customer?.id ?? ""}:${Kpi.ORDERS}`,
  );

  const handleOpenOrder = (orderId: string) => {
    if (!customer) return;
    track(AnalyticsEvents.ORDER_OPENED, { orderId, customerId: customer.id, customerName: customer.name });
    push(generatePath(ActoRoutes.CUSTOMER_ORDER_DETAILS, { id: customer.id, orderId }));
  };

  const showSkeleton = orders.items.length === 0 && orders.isPending;
  const showEmpty = !showSkeleton && orders.isEmpty;
  const isSearchDisabled = showEmpty && !isSearchOpen;
  const firstPageFailed = orders.error !== undefined && orders.items.length === 0;
  const showError = !showSkeleton && firstPageFailed;
  const periodLabel = trend.summary.legend[0]?.range ?? "";

  const listTitle = showsProductGroups ? productList.title : t("title");
  const listFilterLabel = showsProductGroups ? productList.controls.filterLabel : statusFilterLabel;
  const pill = isPastListHeader
    ? { title: listTitle, subtitle: listFilterLabel }
    : isPastFigures
      ? {
          title: t("title"),
          subtitle: orderCount !== undefined ? t("countSubtitle", { count: orderCount }) : undefined,
        }
      : undefined;

  const showsDock = isPastListHeader;
  const isDockSearchDisabled = showsProductGroups
    ? productList.groups.isEmpty && !productList.groups.isSearchActive
    : isSearchDisabled;

  const openSearchFromTopBar = () => {
    if (showsProductGroups) {
      setIsProductSearchOpen(true);
      productListHeaderRef.current?.scrollIntoView({ block: "start" });
      return;
    }
    setIsSearchOpen(true);
    listHeaderRef.current?.scrollIntoView({ block: "start" });
  };

  const switchList = (next: number) => {
    onOrdersListChange(next === ORDERS_LIST ? "orders" : "productGroups");
    setStatusFilterIndex(0);
    resetSearch();
    setIsSearchOpen(false);
    productList.controls.reset();
    setIsProductSearchOpen(false);
    if (next === ORDERS_LIST) drillTo(0);
  };

  const listSwitch = (
    <TitleIndent>
      <SelectMenu
        variant="sectionTitle"
        options={[
          { value: ORDERS_LIST, label: t("title") },
          { value: PRODUCT_GROUPS_LIST, label: tAnalysis("productGroups") },
        ]}
        value={showsProductGroups ? PRODUCT_GROUPS_LIST : ORDERS_LIST}
        onSelect={switchList}
        triggerLabel={t("listSwitch")}
      />
    </TitleIndent>
  );

  const ordersCard = currentLevel ? (
    <KpiPairCard
      subtitle={currentLevel.externalReference ?? customer?.name ?? ""}
      title={currentLevel.name}
      trend={levelTrend.trend}
      figuresRef={observeFigures}
    />
  ) : (
    <KpiPairCard subtitle={customer?.name ?? ""} title={t("title")} trend={trend} figuresRef={observeFigures} />
  );

  const statusFilterMenu = (variant: "icon" | "headerIcon") => (
    <SelectMenu
      variant={variant}
      triggerIcon={QuickFilterIcon}
      align="right"
      options={STATUS_FILTERS.map((status, index) => ({ value: index, label: t(`invoiceStatus.${status ?? "ALL"}`) }))}
      value={statusFilterIndex}
      onSelect={setStatusFilterIndex}
      triggerLabel={t("filter")}
    />
  );

  return (
    <>
      <ShellSlotContent slot="topBarLeading" id="customerOrdersNav">
        {isDrilled ? (
          <AnalysisDrillNav rootJumpLabel={tAnalysis("productGroups")} />
        ) : (
          <Leading>
            <ShellCloseButton to={id ? generatePath(ActoRoutes.CUSTOMER_DETAILS, { id }) : undefined} />
            <PillFade data-visible={isPastFigures} aria-hidden={!isPastFigures}>
              <TitlePill subtitle={pill?.subtitle} subtitleTone="neutral">
                {pill?.title ?? t("title")}
              </TitlePill>
            </PillFade>
          </Leading>
        )}
      </ShellSlotContent>
      <ShellSlotContent slot="topBarTrailing" id="customerOrdersStatusFilter">
        <DockFade data-visible={showsDock} aria-hidden={!showsDock} inert={!showsDock}>
          <TrailingActions>
            <HeaderIconButton
              type="button"
              aria-label={t("search")}
              onClick={openSearchFromTopBar}
              disabled={isDockSearchDisabled}
            >
              <SearchIcon />
            </HeaderIconButton>
            {showsProductGroups ? (
              <>
                <HeaderIconButton
                  ref={productFilterRef}
                  type="button"
                  aria-label={tSearch("filter")}
                  onClick={() => setIsProductFilterOpen((open) => !open)}
                >
                  <QuickFilterIcon />
                </HeaderIconButton>
                <FilterPopover
                  groups={productList.controls.filterMenu.groups}
                  selectedKeys={productList.controls.filterMenu.selectedKeys}
                  onToggle={productList.controls.filterMenu.onToggle}
                  anchorRef={productFilterRef}
                  isOpen={isProductFilterOpen}
                  onClose={() => setIsProductFilterOpen(false)}
                  align="end"
                />
              </>
            ) : (
              statusFilterMenu("headerIcon")
            )}
          </TrailingActions>
        </DockFade>
      </ShellSlotContent>
      <PageLayout
        hPadding="card"
        isLoading={isCustomerPending || trend.isPending}
        isEmpty={!customer && !isCustomerPending}
      >
        {!customer ? (
          <>{tAnalysis("customerNotFound", { defaultValue: "Customer not found" })}</>
        ) : showsProductGroups ? (
          <OrdersProductPath
            card={ordersCard}
            list={productList}
            rootListTitle={listSwitch}
            isTopLevel={!isDrilled}
            headerSentinelRef={observeListHeader}
            listHeaderRef={productListHeaderRef}
            searchExpanded={isProductSearchOpen}
            onSearchExpandedChange={setIsProductSearchOpen}
          />
        ) : (
          <>
            {ordersCard}
            <RowListCard
              transitionKey={showSkeleton ? "loading" : showError ? "error" : showEmpty ? "empty" : "list"}
              header={
                <ListHeaderAnchor ref={listHeaderRef}>
                  <div ref={observeListHeader}>
                    <FilterHeaderRow filterSlot={statusFilterMenu("icon")}>
                      <SearchHeaderRow
                        title={selectedProduct ? <ListTitle>{t("productOrdersTitle")}</ListTitle> : listSwitch}
                        placeholder={t("searchPlaceholder")}
                        value={searchValue}
                        onChange={setSearchValue}
                        expanded={isSearchOpen}
                        onExpandedChange={setIsSearchOpen}
                        disabled={isSearchDisabled}
                        bareSearch
                      />
                    </FilterHeaderRow>
                  </div>
                </ListHeaderAnchor>
              }
            >
              {showSkeleton ? (
                <RowSkeletonList />
              ) : showError ? (
                <RowListEmptyState>{t("loadError")}</RowListEmptyState>
              ) : showEmpty ? (
                <RowListEmptyState>
                  {statusFilter
                    ? t("emptyStatus", { status: statusFilterLabel })
                    : orderNumber.trim()
                      ? t("emptyFiltered")
                      : t("empty", { range: periodLabel })}
                </RowListEmptyState>
              ) : (
                <OrderRows
                  items={orderRows}
                  onOpenOrder={(order) => handleOpenOrder(order.id)}
                  sentinelRef={sentinelRef}
                />
              )}
            </RowListCard>
          </>
        )}
      </PageLayout>
    </>
  );
}
